/* @flow */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';

// Components
import FolderIconWrapper from '../FolderIconWrapper';
import type { Folder_folderPortfolioItems } from './__generated__/Folder_folderPortfolioItems.graphql'; // eslint-disable-line camelcase

// Types
import type { RelayProp } from 'react-relay';
import type { AddToFolderWrapperProps } from '../AddToFolderModalWrapper';
import type { TrackingHooksType } from '../../types';

type ModalComponentType = Class<React.Component<AddToFolderWrapperProps>>;

type FolderProps = {
    additionalButtonClass?: string,
    additionalIconClass?: string,
    allowSavingMultipleItems: boolean,
    closeModal?: () => void,
    currentPortfolioId?: string,
    disabledButton: boolean,
    environment: any,
    getModalComponent: () => Promise<ModalComponentType>,
    isStrokeWidthHeavy?: boolean,
    iconPosition: string,
    iconText?: string,
    loadPortfolioItemData: boolean,
    modalHeaderText: string,
    doRefetch: () => Promise<void>,
    onAddItemError?: (e: ?Error) => void,
    onAddItemSuccess?: (?{ portfolioName?: string }) => void,
    onAddNoteError?: (e: ?Error) => void,
    onAddNoteSuccess?: () => void,
    onCreateFolderError?: (e: ?Error) => void,
    onCreateFolderSuccess?: () => void,
    onFolderClick?: (itemId?: string) => void,
    onFolderRemove?: () => void,
    onFolderSave?: () => void,
    onNewFolderSave?: () => void,
    onRemoveItemError?: (e: ?Error) => void,
    onRemoveItemSuccess?: () => void,
    itemId: string,
    relay: RelayProp,
    selectedItemIds?: Array<string>,
    selectedItemImages?: Array<string>,
    selectedItemTitles?: Array<string>,
    singlePortfolioItem: boolean,
    trackingHooks: TrackingHooksType,
    type: 'outline' | 'button',
    userId?: string,
    userIds?: Array<string>,
    folderPortfolioItems?: Folder_folderPortfolioItems, // eslint-disable-line camelcase
    openOnRender?: boolean,
};

export function Folder(props: FolderProps) {
    const [isAnimated, setIsAnimated] = useState(false);
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [modalIsLoaded, setModalIsLoaded] = useState(false);
    const modalComponentRef = useRef(null);

    const {
        closeModal,
        doRefetch,
        isStrokeWidthHeavy,
        onAddItemSuccess,
        onRemoveItemSuccess,
        openOnRender,
        getModalComponent,
    } = props;

    const openModal = useCallback(() => {
        if (modalIsLoaded) {
            // Modal Component is already required
            setModalIsOpen(true);
        } else {
            // Modal Component is not yet required
            getModalComponent()
                .then(ModalComponent => {
                    modalComponentRef.current = ModalComponent;
                    setModalIsOpen(true);
                    setModalIsLoaded(true);
                })
                .catch(err => {
                    // eslint-disable-next-line
                    console.warn('failed to get ModalComponent', err);
                });
        }
    }, [modalIsLoaded, getModalComponent]);

    function handleFolderClick(e: SyntheticEvent<*>) {
        e.preventDefault();
        const trackingHooks = props.trackingHooks || {};

        if (props.onFolderClick) {
            props.onFolderClick();
        }
        if (trackingHooks.trackFolderClick) {
            trackingHooks.trackFolderClick();
        }

        openModal();
    }

    function handleCloseModal() {
        return new Promise(resolve => {
            setModalIsOpen(false);
            doRefetch().then(resolve);
        });
    }

    function handleAddItem(portfolio) {
        if (onAddItemSuccess) {
            onAddItemSuccess(portfolio);
        }
        setIsAnimated(true);
    }

    function handleRemoveItem() {
        if (onRemoveItemSuccess) {
            onRemoveItemSuccess();
        }
        setIsAnimated(true);
    }

    const hasOpenedOnRender = useRef(false);

    useEffect(() => {
        if (openOnRender && !hasOpenedOnRender.current) {
            openModal();
            hasOpenedOnRender.current = true;
        }
    }, [openOnRender, openModal]);

    useEffect(() => {
        if (!modalIsOpen && closeModal) {
            closeModal();
        }
    }, [closeModal, modalIsOpen]);

    const { folderPortfolioItems = [] } = props;
    const isInFolder = !!folderPortfolioItems.length;
    const ThisModalComponent = modalComponentRef.current;

    return (
        <React.Fragment>
            <FolderIconWrapper
                additionalButtonClass={props.additionalButtonClass}
                additionalIconClass={props.additionalIconClass}
                disabledButton={props.disabledButton}
                iconPosition={props.iconPosition || 'absolute'}
                iconText={props.iconText}
                isAnimated={isAnimated}
                isInFolder={isInFolder}
                isStrokeWidthHeavy={isStrokeWidthHeavy}
                type={props.type}
                onAnimationEnd={() => setIsAnimated(false)}
                onClick={handleFolderClick}
            />
            {modalIsLoaded && modalIsOpen && props.userId && ThisModalComponent && (
                <ThisModalComponent
                    allowSavingMultipleItems={props.allowSavingMultipleItems}
                    closeModal={handleCloseModal}
                    currentPortfolioId={props.currentPortfolioId}
                    environment={props.relay.environment}
                    loadPortfolioItemData={props.loadPortfolioItemData}
                    modalHeaderText={props.modalHeaderText || 'Save to Folder'}
                    isOpen={modalIsOpen}
                    onAddItemError={props.onAddItemError}
                    onAddItemSuccess={handleAddItem}
                    onAddNoteError={props.onAddNoteError}
                    onAddNoteSuccess={props.onAddNoteSuccess}
                    onCreateFolderError={props.onCreateFolderError}
                    onCreateFolderSuccess={props.onCreateFolderSuccess}
                    onFolderRemove={props.onFolderRemove}
                    onFolderSave={props.onFolderSave}
                    onNewFolderSave={props.onNewFolderSave}
                    onRemoveItemError={props.onRemoveItemError}
                    onRemoveItemSuccess={handleRemoveItem}
                    primaryItemPk={props.itemId}
                    selectedItemIds={props.selectedItemIds}
                    selectedItemImages={props.selectedItemImages}
                    selectedItemTitles={props.selectedItemTitles}
                    singlePortfolioItem={props.singlePortfolioItem}
                    trackingHooks={props.trackingHooks}
                    userId={props.userId}
                    userIds={props.userIds || []}
                />
            )}
        </React.Fragment>
    );
}

export default createFragmentContainer(Folder, {
    folderPortfolioItems: graphql`
        fragment Folder_folderPortfolioItems on PortfolioItemV2 @relay(plural: true) {
            # we only check for the presence of a portfolio item
            __typename
        }
    `,
});
